import { useMemo } from 'react';

import { LoadedData } from '@occc/utils';
import {
  CustomerScanDataOverviewFragment,
  ScanDataSubmissionHealthMetricsFragment,
} from '@occc/graphql';

import { ScanDataOverview } from './scan-data-overview.component';
import { useScanDataMetricTabsData } from './hooks/use-scan-data-metric-tabs-data.hook';
import { useScanDataStoreDetailsTableSearchAndPagination } from './hooks/use-scan-data-store-details-table-search-and-pagination.hook';
import { useScanDataStoreDetailsTableData } from './hooks/use-scan-data-store-details-table-data.hook';
import { useScanDataDownloadSubmissionCSV } from './hooks/use-scan-data-download-submission-csv.hook';

import { useCustomerScanDataOverview } from '../../hooks/use-customer-scan-data-overview.hook';
import { useScanDataSubmissionHealthMetrics } from '../../hooks/use-scan-data-submission-health-metrics.hook';

export type ScanDataOverviewControllerProps = {
  submissionDateEquals: string;
};

export const ScanDataOverviewController = ({
  submissionDateEquals,
}: ScanDataOverviewControllerProps) => {
  const customerScanDataOverview = useCustomerScanDataOverview();

  const customerScanDataOverviewLoadedData: LoadedData<CustomerScanDataOverviewFragment> =
    useMemo(() => {
      return {
        data: customerScanDataOverview.data?.customerScanDataOverview,
        loading: customerScanDataOverview.loading,
        error: customerScanDataOverview.error,
      };
    }, [
      customerScanDataOverview.data?.customerScanDataOverview,
      customerScanDataOverview.error,
      customerScanDataOverview.loading,
    ]);

  const submissionHealthMetrics = useScanDataSubmissionHealthMetrics({
    filter: {
      submissionDateEquals,
    },
  });

  const submissionHealthMetricsLoadedData: LoadedData<ScanDataSubmissionHealthMetricsFragment> =
    useMemo(() => {
      return {
        data: submissionHealthMetrics.data?.scanDataSubmissionHealthMetrics,
        loading: submissionHealthMetrics.loading,
        error: submissionHealthMetrics.error,
      };
    }, [
      submissionHealthMetrics.data?.scanDataSubmissionHealthMetrics,
      submissionHealthMetrics.error,
      submissionHealthMetrics.loading,
    ]);

  const {
    metricsData,
    selectedMetric,
    handleMetricChange,
    scanDataMetricsTimeSeriesLoadedData,
    selectedMetricChartData,
  } = useScanDataMetricTabsData(submissionDateEquals);

  const searchAndPagination = useScanDataStoreDetailsTableSearchAndPagination();
  const { searchValueByMetric, searchSetterByMetric } = searchAndPagination;

  const {
    submittedTransactionsTableData,
    missingStoresTableData,
    reimbursementsTableData,
    reportedTransactionsTableData,
  } = useScanDataStoreDetailsTableData(submissionDateEquals, searchAndPagination);

  const searchValue = useMemo(() => {
    return searchValueByMetric[selectedMetric.metricType];
  }, [selectedMetric.metricType, searchValueByMetric]);

  const onSearchChange = useMemo(() => {
    return searchSetterByMetric[selectedMetric.metricType];
  }, [selectedMetric.metricType, searchSetterByMetric]);

  const [handleDownloadCSV, isDownloading] = useScanDataDownloadSubmissionCSV(
    selectedMetric.metricType,
    submissionDateEquals,
  );

  return (
    <ScanDataOverview
      customerScanDataOverviewLoadedData={customerScanDataOverviewLoadedData}
      submissionHealthMetricsLoadedData={submissionHealthMetricsLoadedData}
      metricsData={metricsData}
      submittedTransactionsTableData={submittedTransactionsTableData}
      missingStoresTableData={missingStoresTableData}
      reimbursementsTableData={reimbursementsTableData}
      reportedTransactionsTableData={reportedTransactionsTableData}
      handleMetricChange={handleMetricChange}
      selectedMetric={selectedMetric}
      onDownloadCSV={handleDownloadCSV}
      isDownloading={isDownloading}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      scanDataMetricsTimeSeriesLoadedData={scanDataMetricsTimeSeriesLoadedData}
      selectedMetricChartData={selectedMetricChartData}
    />
  );
};
